/**
 * Pure utility functions for the bunny-player component.
 * No side effects, no state, fully testable without mocks.
 *
 * @requirements
 * - REQ-VIDEO-101: Video playback formatting utilities
 */

/**
 * Format seconds into a human-readable MM:SS string.
 */
export function formatTime(seconds: number): string {
  if (!seconds || isNaN(seconds)) return '0:00';

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
}

/** What the player should do with an hls.js error event. */
export type HlsErrorAction = 'ignore' | 'retry' | 'recover' | 'fail';

/**
 * KAN-576: classify an hls.js error into the player's response.
 *
 * A per-org Bunny video requested on the wrong CDN host 404s on
 * playlist.m3u8. hls.js reports that as a FATAL networkError whose `details`
 * name a manifest failure. The player used to answer every fatal networkError
 * with `startLoad()` -- an unbounded retry -- so a manifest that would never
 * load produced a blank grey player at 0:00 / 0:00 and no message at all,
 * which is what made this bug cost an hour to diagnose.
 *
 * A manifest that will not load is terminal: there is no fragment to fall back
 * to and nothing downstream can recover it. Fragment-level network errors are
 * still retryable (the manifest loaded, a single segment did not), and media
 * errors still go to hls.js's own media recovery.
 */
export const MAX_HLS_NETWORK_RETRIES = 3;

export function classifyHlsError(
  fatal: boolean,
  type: string,
  details: string,
  retriesUsed: number = 0,
  maxRetries: number = MAX_HLS_NETWORK_RETRIES
): HlsErrorAction {
  if (!fatal) return 'ignore';

  const isManifestFailure = typeof details === 'string' && details.startsWith('manifest');

  if (type === 'networkError') {
    if (isManifestFailure) return 'fail';
    // A fragment CAN recover, but not forever: an unbounded startLoad() loop is
    // what let a broken stream spin behind a blank player indefinitely. Once the
    // budget is spent, say so instead of retrying again.
    return retriesUsed >= maxRetries ? 'fail' : 'retry';
  }
  if (type === 'mediaError') {
    return 'recover';
  }
  // Anything else fatal is unrecoverable -- report it rather than swallow it.
  return 'fail';
}

/** HTMLMediaElement.error.code for "source not supported" (also "Empty src attribute"). */
export const MEDIA_ERR_SRC_NOT_SUPPORTED = 4;

/**
 * KAN-576 hotfix (2026-09-12): is this `<video>` error the browser rejecting
 * an EMPTY placeholder src, rather than a real source failing?
 *
 * On the HLS path the element is rendered before hls.js attaches its blob URL,
 * and Chrome answers the interim empty src with MEDIA_ERR_SRC_NOT_SUPPORTED
 * ("Empty src attribute") ~35ms later -- after processedVideoUrl is already
 * set, so the "no URL yet" guard could not catch it. The first day this
 * handler was live, every learner on every HLS module saw "Video Unavailable"
 * painted over a stream that was playing fine underneath.
 *
 * The discriminator is `currentSrc`: the placeholder error fires before the
 * element has selected any resource, so currentSrc is ''. A genuine
 * SRC_NOT_SUPPORTED (an iOS native-HLS 404, an unsupported container) fires
 * after resource selection, so currentSrc names the URL that failed.
 */
export function isEmptySrcPlaceholderError(
  errorCode: number | null | undefined,
  currentSrc: string
): boolean {
  return errorCode === MEDIA_ERR_SRC_NOT_SUPPORTED && currentSrc === '';
}

/** How the player attaches an HLS stream. */
export type HlsPlaybackPath = 'native' | 'hlsjs' | 'unsupported';

/**
 * KAN-635: pick the HLS playback path.
 *
 * hls.js is preferred because Chrome can answer "maybe" to canPlayType for
 * HLS and then fail to play it. But hls.js 1.6 also reports itself supported
 * on iPhone (iOS 17.1+) through ManagedMediaSource, and the native iPhone
 * fullscreen player on an MSE-backed video only flashes open and shut. So an
 * iPhone that can play HLS natively always does.
 */
export function chooseHlsPlaybackPath(
  hlsJsSupported: boolean,
  nativeHlsSupport: string,
  iphoneClass: boolean
): HlsPlaybackPath {
  if (iphoneClass && nativeHlsSupport !== '') return 'native';
  if (hlsJsSupported) return 'hlsjs';
  return nativeHlsSupport !== '' ? 'native' : 'unsupported';
}
